import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { documentsApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  FileText,
  Plus,
  Upload,
  Search,
  Filter,
  Download,
  Eye,
  Trash2,
  FolderOpen,
  File,
  FileSpreadsheet,
  FileImage,
  FileType,
  Clock,
  User,
  Building2,
  Globe,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
  policy: { label: 'HR Policies', icon: FileText, color: 'bg-blue-100 text-blue-700' },
  contract: { label: 'Contracts', icon: File, color: 'bg-green-100 text-green-700' },
  template: { label: 'Templates', icon: FileSpreadsheet, color: 'bg-purple-100 text-purple-700' },
  handbook: { label: 'Handbooks', icon: FolderOpen, color: 'bg-amber-100 text-amber-700' },
  form: { label: 'Forms', icon: FileType, color: 'bg-red-100 text-red-700' }
};

const FILE_TYPE_ICONS = {
  pdf: FileType,
  docx: FileText,
  xlsx: FileSpreadsheet,
  png: FileImage,
  jpg: FileImage
};

export default function DocumentsPage() {
  const { user, canAccessHR } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [docForm, setDocForm] = useState({
    title: '',
    description: '',
    category: 'policy',
    file_type: 'pdf',
    is_public: true,
    department: ''
  });

  useEffect(() => {
    fetchDocuments();
  }, [filterCategory]);

  const fetchDocuments = async () => {
    try {
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const res = await documentsApi.getAll(params);
      setDocuments(res.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    try {
      await documentsApi.upload(docForm);
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to archive this document?')) return;
    
    try {
      await documentsApi.delete(docId);
      toast.success('Document archived');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    // If a real file URL exists, just open it
    if (doc.file_url && doc.file_url.trim()) {
      const link = window.document.createElement('a');
      link.href = doc.file_url;
      link.download = doc.title || 'document';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      return;
    }

    // Otherwise generate a demo PDF on-the-fly so the action still works
    try {
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF();
      const margin = 16;
      let y = 24;

      pdf.setFillColor(26, 54, 93); // navy
      pdf.rect(0, 0, 210, 14, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11);
      pdf.text('Karya HRMS · Document', margin, 9);

      pdf.setTextColor(26, 54, 93);
      pdf.setFontSize(20);
      pdf.text(doc.title || 'Document', margin, y);
      y += 8;

      pdf.setTextColor(100, 116, 139);
      pdf.setFontSize(10);
      const meta = [
        `Category: ${doc.category || '-'}`,
        `Version: ${doc.version || '1.0'}`,
        `Uploaded by: ${doc.uploaded_by || '-'}`,
        `Created: ${doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}`,
      ].join('   |   ');
      pdf.text(meta, margin, y);
      y += 10;

      pdf.setDrawColor(45, 212, 191);
      pdf.setLineWidth(0.6);
      pdf.line(margin, y, 210 - margin, y);
      y += 8;

      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(12);
      const description = doc.description || 'This is a demo document generated by Karya HRMS. Upload a real file to replace this placeholder.';
      const split = pdf.splitTextToSize(description, 180);
      pdf.text(split, margin, y);
      y += split.length * 6 + 6;

      pdf.setTextColor(148, 163, 184);
      pdf.setFontSize(9);
      pdf.text('Generated by Karya HRMS demo. Replace with the actual file in production.', margin, 285);

      pdf.save(`${(doc.title || 'document').replace(/[^a-z0-9_-]+/gi, '_')}.pdf`);
      toast.success('Demo file downloaded');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download document');
    }
  };

  const resetForm = () => {
    setDocForm({
      title: '',
      description: '',
      category: 'policy',
      file_type: 'pdf',
      is_public: true,
      department: ''
    });
  };

  const filteredDocuments = documents.filter(doc =>
    !searchTerm || 
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const category = doc.category || 'policy';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(doc);
    return acc;
  }, {});

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFileIcon = (fileType) => {
    const Icon = FILE_TYPE_ICONS[fileType] || FileText;
    return Icon;
  };

  const totalDocs = documents.length;
  const publicDocs = documents.filter(d => d.is_public).length;

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="page-title">Document Management</h1>
          <p className="page-description">Access HR policies, templates, and important documents</p>
        </div>
        {canAccessHR && (
          <Button className="btn-primary gap-2" onClick={() => setShowUploadModal(true)} data-testid="upload-doc-btn">
            <Upload className="h-4 w-4" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="stat-card border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Documents</p>
                <p className="text-3xl font-bold text-blue-600">{totalDocs}</p>
              </div>
              <FolderOpen className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Public Access</p>
                <p className="text-3xl font-bold text-green-600">{publicDocs}</p>
              </div>
              <Globe className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Categories</p>
                <p className="text-3xl font-bold text-purple-600">{Object.keys(groupedDocuments).length}</p>
              </div>
              <FileText className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Private</p>
                <p className="text-3xl font-bold text-amber-600">{totalDocs - publicDocs}</p>
              </div>
              <Lock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filteredDocuments.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FolderOpen className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">No documents found</p>
              {canAccessHR && (
                <Button className="mt-4 gap-2" onClick={() => setShowUploadModal(true)}>
                  <Upload className="h-4 w-4" />
                  Upload First Document
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedDocuments).map(([category, docs]) => {
            const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.policy;
            const CategoryIcon = config.icon;
            
            return (
              <Card key={category}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CategoryIcon className="h-5 w-5" />
                    {config.label}
                    <Badge variant="secondary">{docs.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {docs.map(doc => {
                      const FileIcon = getFileIcon(doc.file_type);
                      return (
                        <div
                          key={doc.id}
                          className="p-4 border rounded-lg hover:border-[#1E88E5]/50 transition-colors bg-card"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${config.color}`}>
                              <FileIcon className="h-6 w-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium truncate">{doc.title}</h3>
                              {doc.description && (
                                <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                                  {doc.description}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatDate(doc.created_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {doc.uploaded_by}
                                </span>
                                {doc.is_public ? (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <Globe className="h-3 w-3" />
                                    Public
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-amber-600">
                                    <Lock className="h-3 w-3" />
                                    Private
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                            <Button variant="ghost" size="sm" className="flex-1 gap-1" onClick={() => handleDownload(doc)} data-testid={`view-doc-${doc.id}`}>
                              <Eye className="h-4 w-4" />
                              View
                            </Button>
                            <Button variant="ghost" size="sm" className="flex-1 gap-1" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                              <Download className="h-4 w-4" />
                              Download
                            </Button>
                            {canAccessHR && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDelete(doc.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#1E88E5]" />
              Upload Document
            </DialogTitle>
            <DialogDescription>Add a new document to the repository</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>Document Title *</Label>
              <Input
                required
                value={docForm.title}
                onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                placeholder="e.g., Employee Handbook 2025"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={docForm.description}
                onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                placeholder="Brief description of the document..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={docForm.category}
                  onValueChange={(v) => setDocForm({ ...docForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>File Type</Label>
                <Select
                  value={docForm.file_type}
                  onValueChange={(v) => setDocForm({ ...docForm, file_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="docx">Word Document</SelectItem>
                    <SelectItem value="xlsx">Excel Spreadsheet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Department (Optional)</Label>
              <Input
                value={docForm.department}
                onChange={(e) => setDocForm({ ...docForm, department: e.target.value })}
                placeholder="Leave blank for all departments"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_public"
                checked={docForm.is_public}
                onChange={(e) => setDocForm({ ...docForm, is_public: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_public" className="font-normal">
                Make this document available to all employees
              </Label>
            </div>
            <div className="p-4 border-2 border-dashed rounded-lg text-center">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                File upload will be available after connecting storage
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Currently, documents are created as placeholders
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Upload Document
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
